import React, { useState, useRef, useEffect, useContext } from 'react';
import Card from 'react-bootstrap/Card';
import Button from 'react-bootstrap/Button';
import { useNavigate, useLocation } from 'react-router-dom';
import { GlobalStateContext } from './GlobalState';
import './ChatBox.css';

// Simple markdown parser for basic formatting
const parseMarkdown = (text) => {
  if (!text) return '';
  return text
    // Bold text **text** or __text__
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/__(.*?)__/g, '<strong>$1</strong>')
    // Italic text *text* or _text_
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/_(.*?)_/g, '<em>$1</em>')
    // Code `code`
    .replace(/`(.*?)`/g, '<code>$1</code>')
    // Line breaks
    .replace(/\n/g, '<br>')
    // Links [text](url)
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
};

const ChatBox = ({ isOpen, onToggle, initialContext }) => {
  // Load messages from localStorage or use default welcome message
  const [messages, setMessages] = useState(() => {
    try {
      const savedMessages = localStorage.getItem('chatMessages');
      if (savedMessages) {
        const parsed = JSON.parse(savedMessages);
        // Convert timestamp strings back to Date objects
        return parsed.map(msg => ({
          ...msg,
          timestamp: new Date(msg.timestamp)
        }));
      }
    } catch (error) {
      console.error('Error loading chat messages from localStorage:', error);
    }
    // Return default welcome message if nothing in localStorage
    return [
      { id: 1, text: "**Hi! 👋** I'm here to help you build your playlist.\n\nTell me what *genre* of music you like or make some example of *authors* or *songs* that you like. You can also specify the *mood*, *years*, or *topics* that you wish to include in your playlist. \n\n", sender: 'bot', timestamp: new Date() }
    ];
  });
  const [inputText, setInputText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isClearingChat, setIsClearingChat] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { setGlobalList, globalList } = useContext(GlobalStateContext);
  const lastRouteRef = useRef(location.pathname);
  const hasCheckedOnMount = useRef(false);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  };

  // Helper function to add playlist message to chat
  const addPlaylistMessage = (playlist) => {
    if (!playlist || playlist.length === 0) return;

    const playlistMsg = {
      id: Date.now(), // Use timestamp for unique ID
      text: '', // Empty text for playlist messages
      sender: 'system',
      type: 'playlist',
      timestamp: new Date(),
      playlistData: {
        songs: playlist,
        count: playlist.length,
        duration: Math.round(playlist.reduce((acc, s) => acc + (s.duration_s || 0), 0) / 60)
      }
    };

    setMessages(prev => [...prev, playlistMsg]);
  };

  useEffect(() => {
    // Only scroll the chat area, not the whole page
    setTimeout(() => {
      scrollToBottom();
    }, 50);
  }, [messages]);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('chatMessages', JSON.stringify(messages));
    } catch (error) {
      console.error('Error saving chat messages to localStorage:', error);
    }
  }, [messages]);

  // Handle initial context if provided
  useEffect(() => {
    if (initialContext) {
      const contextMsg = {
        id: Date.now(), // Use timestamp as ID to avoid collision
        text: `**Context:** ${initialContext}`,
        sender: 'user', // Or 'system' if supported, but 'user' makes it visible as input
        timestamp: new Date()
      };

      // Check if we already have this context to avoid duplicates (simple check)
      const alreadyHasContext = messages.some(m => m.text === contextMsg.text);
      if (!alreadyHasContext) {
        setMessages(prev => [...prev, contextMsg]);
      }
    }
  }, [initialContext]); // Only run when initialContext changes

  // Check on mount if current playlist should be displayed
  useEffect(() => {
    if (hasCheckedOnMount.current) return;
    hasCheckedOnMount.current = true;

    // Only check if we have a playlist loaded and we're on chat/builder routes
    if ((location.pathname === '/chat' || location.pathname === '/builder') &&
        globalList.length > 0) {

      // Find the last playlist message
      const lastPlaylistMessage = messages.slice().reverse().find(m => m.type === 'playlist');

      // Check if playlist is different from last shown
      const playlistChanged = !lastPlaylistMessage ||
        JSON.stringify(lastPlaylistMessage.playlistData.songs.map(s => s.id)) !==
        JSON.stringify(globalList.map(s => s.id));

      if (playlistChanged) {
        // Add text message first
        const textMsg = {
          id: Date.now(),
          text: "This is the current playlist:",
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, textMsg]);

        // Then add playlist message with small delay
        setTimeout(() => {
          addPlaylistMessage(globalList);
        }, 100);
      }
    }
  }, []); // Empty deps - run only on mount

  // Detect when user returns from /mixandsave to add playlist message
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = lastRouteRef.current;

    // Only trigger if actually changed paths
    if (currentPath !== previousPath &&
        (currentPath === '/chat' || currentPath === '/builder') &&
        globalList.length > 0) {

      // Find the last playlist message to compare
      const lastPlaylistMessage = messages.slice().reverse().find(m => m.type === 'playlist');

      // Check if playlist changed by comparing song IDs
      const playlistChanged = !lastPlaylistMessage ||
        JSON.stringify(lastPlaylistMessage.playlistData.songs.map(s => s.id)) !==
        JSON.stringify(globalList.map(s => s.id));

      if (playlistChanged) {
        // Add text message first
        const textMsg = {
          id: Date.now(),
          text: "This is the current playlist:",
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, textMsg]);

        // Then add playlist message with small delay
        setTimeout(() => {
          addPlaylistMessage(globalList);
        }, 100);
      }
    }

    lastRouteRef.current = currentPath;
  }, [location.pathname, globalList, messages, addPlaylistMessage]);

  const handleSendMessage = async () => {
    if (inputText.trim() && !isLoading) {
      const newMessage = {
        id: messages.length + 1,
        text: inputText,
        sender: 'user',
        timestamp: new Date()
      };

      const updatedMessages = [...messages, newMessage];
      setMessages(updatedMessages);
      const currentInput = inputText;
      setInputText('');
      setIsLoading(true);

      // Retain focus on input after clearing text
      setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }, 0);

      // Call the API for bot response
      try {
        const { textBefore, textAfter, playlist } = await getBotResponse(currentInput, updatedMessages);

        // Add textBefore message if it exists
        if (textBefore && textBefore.trim()) {
          const botResponseBefore = {
            id: Date.now(),
            text: textBefore,
            sender: 'bot',
            timestamp: new Date()
          };
          setMessages(prev => [...prev, botResponseBefore]);
        }

        // Add playlist message with small delay
        if (playlist && playlist.length > 0) {
          setTimeout(() => {
            addPlaylistMessage(playlist);
          }, 100);
        }

        // Add textAfter message with delay after playlist
        if (textAfter && textAfter.trim()) {
          setTimeout(() => {
            const botResponseAfter = {
              id: Date.now() + 1,
              text: textAfter,
              sender: 'bot',
              timestamp: new Date()
            };
            setMessages(prev => [...prev, botResponseAfter]);
          }, 200);
        }

        // Ensure focus stays on input after all messages
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.focus();
          }
        }, 300);

      } catch (error) {
        console.error('Error getting bot response:', error);
        const errorResponse = {
          id: updatedMessages.length + 1,
          text: "Sorry, I'm having trouble connecting right now. Please try again later.",
          sender: 'bot',
          timestamp: new Date()
        };
        setMessages(prev => [...prev, errorResponse]);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getBotResponse = async (userMessage, messageHistory) => {
    try {
      // Use the new /chat endpoint which handles both chat and context
      const response = await fetch('/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message: userMessage,
          session_id: localStorage.getItem('chat_session_id'), // Ensure session persistence
          systemContext: globalList // Pass the current playlist from GlobalState
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Store session ID if returned
      if (data.session_id) {
        localStorage.setItem('chat_session_id', data.session_id);
      }

      // Handle updated playlist if present
      if (data.updated_playlist) {
        console.log("Received updated playlist from agent:", data.updated_playlist);
        setGlobalList(data.updated_playlist);
      }

      return {
        textBefore: data.textBefore || "",
        textAfter: data.textAfter || "",
        playlist: data.updated_playlist || null
      };
    } catch (error) {
      console.error('API call failed:', error);
      return getLocalBotResponse(userMessage);
    }
  };

  const getLocalBotResponse = (userMessage) => {
    return {
      textBefore: "Unfortunately **the connection with AI Chat engine is not working properly ** 🎶\n\nYou can try again  in a while or :\n- Use the **search feature** above to find specific songs\n- Try the **Feel Lucky** option for personalized recommendations (only if you are logged-in)\n- Browse the predefined playlists below\n\n",
      textAfter: "",
      playlist: null
    };
  };

  const handleClearChat = async () => {
    setIsClearingChat(true);
    try {
      await fetch('/clear-chat-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
    } catch (error) {
      console.error('Error calling clear-chat-session:', error);
    } finally {
      localStorage.removeItem('chatMessages');
      setMessages([
        { id: 1, text: "**Hi! 👋** I'm here to help you build your playlist.\n\nTell me what *genre* of music you like or make some example of *authors* or *songs* that you like. You can also specify the *mood*, *years*, or *topics* that you wish to include in your playlist. \n\n", sender: 'bot', timestamp: new Date() }
      ]);
      setIsClearingChat(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className={`chat-container ${isOpen ? 'chat-open' : 'chat-closed'}`}>
      <div className="chat-toggle-header" onClick={onToggle}>
        <i className="bi bi-chat-dots me-2"></i>
        <span>Playlist Assistant</span>
        <i className={`bi ${isOpen ? 'bi-chevron-down' : 'bi-chevron-up'} ms-auto`}></i>
      </div>

      {isOpen && (
        <Card className="chat-card">
          <Card.Body className="chat-body">
            <div className="chat-messages">
              {messages.map((message) => {
                // Special rendering for playlist messages
                if (message.type === 'playlist') {
                  return (
                    <div key={message.id} className="message bot playlist">
                      <div className="message-content">
                        <div className="message-text playlist-message">
                          <div className="playlist-header-inline">
                            <i className="bi bi-music-note-list me-2"></i>
                            <strong>Current Playlist</strong>
                            <span className="ms-2">({message.playlistData.count} songs, {message.playlistData.duration} min)</span>
                          </div>
                          <div className="playlist-songs">
                            {message.playlistData.songs.map((song, idx) => (
                              <div key={idx} className="playlist-song-item">
                                <span className="song-number">{idx + 1}.</span>
                                <span>{song.artist} - {song.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="message-time">
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  );
                }

                // Regular message rendering
                return (
                  <div key={message.id} className={`message ${message.sender}`}>
                    <div className="message-content">
                      <div
                        className="message-text"
                        dangerouslySetInnerHTML={{ __html: parseMarkdown(message.text) }}
                      />
                      <div className="message-time">
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </div>
                  </div>
                );
              })}

              {isLoading && (
                <div className="message bot">
                  <div className="message-content">
                    <div className="message-text loading-message">
                      <div className="spinner-border spinner-border-sm me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Thinking...
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input">
              <form onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}>
                <div className="input-group">
                  <input
                    ref={inputRef}
                    type="text"
                    className="form-control"
                    value={inputText}
                    onChange={(e) => setInputText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Ask about music recommendations..."
                  />
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={!inputText.trim() || isLoading}
                  >
                    {isLoading ? (
                      <div className="spinner-border spinner-border-sm" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    ) : (
                      <i className="bi bi-send"></i>
                    )}
                  </button>
                </div>
              </form>

              <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
                <Button
                  variant="success"
                  onClick={() => navigate('/mixandsave')}
                  disabled={globalList.length === 0}
                  className="load-songs-btn"
                >
                  <i className="bi bi-list-check me-2"></i>
                  Check Loaded Songs
                </Button>

                <Button
                  variant="outline-danger"
                  onClick={handleClearChat}
                  disabled={isClearingChat || messages.length <= 1}
                  size="sm"
                >
                  {isClearingChat ? (
                    <div className="spinner-border spinner-border-sm" role="status" />
                  ) : (
                    <i className="bi bi-trash"></i>
                  )}
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
};

export default ChatBox;
